import { faHeartbeat, faArrowsRepeat } from '@fortawesome/pro-light-svg-icons';
import { faHouse } from '@fortawesome/pro-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { faCircleUser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EventContentArg } from '@fullcalendar/core';
import { Avatar, Box, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { status } from '@spike/appointment-model';
import clsx from 'clsx';
import { Chip, Spinner } from 'components/UI';
import Service from 'model/Service';
import moment from 'moment-timezone';
import { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

interface EventFullCalendarProps extends EventContentArg {
    loading?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            color: 'black',
            height: '100%',
            width: '100%',
            border: '1px solid rgba(255,255,255,0)',
            overflowX: 'hidden',
            overflowY: 'hidden',
            containerType: 'size',
            containerName: 'eventCard',

            [theme.breakpoints.down('sm')]: {
                borderRadius: '4px',
                padding: '6px',
                paddingTop: '2px'
            },
            [theme.breakpoints.only('md')]: {
                borderRadius: '6px',
                padding: '7px',
                paddingTop: '4px'
            },
            [theme.breakpoints.only('lg')]: {
                borderRadius: '8px',
                padding: '8px',
                paddingTop: '6px'
            },
            [theme.breakpoints.only('xl')]: {
                borderRadius: '10px',
                padding: '10px',
                paddingTop: '8px'
            }
        },
        spinnerContainer: {
            display: 'flex',
            justifyCOntent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%'
        },
        spinner: {
            [theme.breakpoints.down('sm')]: {
                height: '16px !important',
                width: '16px !important'
            },
            [theme.breakpoints.only('md')]: {
                height: '18px !important',
                width: '18px !important'
            },
            [theme.breakpoints.only('lg')]: {
                height: '24px !important',
                width: '24px !important'
            },
            [theme.breakpoints.only('xl')]: {
                height: '28px !important',
                width: '28px !important'
            }
        },
        booked: {
            'backgroundColor': 'white',
            'border': '2px solid #EAB464',
            '&:hover': {
                cursor: 'pointer',
                borderColor: 'black'
            }
        },
        confirmed: {
            'backgroundColor': '#F5DBB5',
            'border': '2px solid white',
            '&:hover': {
                cursor: 'pointer',
                borderColor: 'black'
            }
        },
        checkedIn: {
            'backgroundColor': '#ECBB74',
            'border': '2px solid white',
            '&:hover': {
                cursor: 'pointer',
                borderColor: 'black'
            }
        },
        inProgress: {
            'backgroundColor': '#BDD2CA',
            'border': '2px solid white',
            '&:hover': {
                cursor: 'pointer',
                borderColor: 'black'
            }
        },
        readyForCheckout: {
            'backgroundColor': '#A6BAC8',
            'border': '2px solid white',
            '&:hover': {
                cursor: 'pointer',
                borderColor: 'black'
            }
        },
        payment: {
            'backgroundColor': 'white',
            'border': '2px solid #444444',
            '&:hover': {
                cursor: 'pointer',
                borderColor: 'black'
            }
        },
        completed: {
            'backgroundColor': '#F1F1F1',
            'border': '2px solid #F1F1F1',
            '&:hover': {
                cursor: 'pointer',
                borderColor: 'black'
            }
        },
        requested: {
            'backgroundColor': '#FFF8DD',
            'border': '1px solid transparent',

            '&:hover': {
                cursor: 'pointer',
                borderColor: 'black'
            }
        },
        loading: {
            '&:hover': {
                cursor: 'default'
            }
        },
        petName: {
            fontFamily: 'Poppins',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden !important',
            wordWrap: 'break-word',
            [theme.breakpoints.down('sm')]: {
                fontSize: '7px'
            },
            [theme.breakpoints.only('md')]: {
                fontSize: '9px'
            },
            [theme.breakpoints.only('lg')]: {
                fontSize: '11px'
            },
            [theme.breakpoints.only('xl')]: {
                fontSize: '13px'
            }
        },
        text: {
            fontFamily: 'Poppins',
            fontWeight: 400,
            whiteSpace: 'nowrap',
            overflow: 'hidden !important',
            wordWrap: 'break-word',
            [theme.breakpoints.down('sm')]: {
                fontSize: '6px'
            },
            [theme.breakpoints.only('md')]: {
                fontSize: '8px'
            },
            [theme.breakpoints.only('lg')]: {
                fontSize: '10px'
            },
            [theme.breakpoints.only('xl')]: {
                fontSize: '12px'
            }
        },
        cardContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%'
        },
        spacedContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%'
        },
        petNameContainer: {
            width: '50% !important',
            [theme.breakpoints.down('sm')]: {
                marginRight: '2px'
            },
            [theme.breakpoints.only('md')]: {
                marginRight: '4px'
            },
            [theme.breakpoints.only('lg')]: {
                marginRight: '6px'
            },
            [theme.breakpoints.only('xl')]: {
                marginRight: '8px'
            }
        },
        row: {
            display: 'flex',
            width: '100%'
        },
        heartIcon: {
            color: '#EF4F57',
            [theme.breakpoints.down('sm')]: {
                fontSize: '8px',
                marginLeft: '2px'
            },
            [theme.breakpoints.only('md')]: {
                fontSize: '10px',
                marginTop: '1px',
                marginLeft: '3px'
            },
            [theme.breakpoints.only('lg')]: {
                fontSize: '12px',
                marginTop: '2px',
                marginLeft: '4px'
            },
            [theme.breakpoints.only('xl')]: {
                fontSize: '14px',
                marginTop: '3px',
                marginLeft: '6px'
            }
        },
        specialCareIcon: {
            color: '#ECC915'
        },
        userIcon: {
            marginRight: '3px',
            [theme.breakpoints.down('sm')]: {
                fontSize: '8px'
            },
            [theme.breakpoints.only('md')]: {
                fontSize: '10px'
            },
            [theme.breakpoints.only('lg')]: {
                fontSize: '12px',
                marginTop: '1px'
            },
            [theme.breakpoints.only('xl')]: {
                fontSize: '14px',
                marginTop: '2px'
            }
        },
        spacing: {
            marginRight: '3px'
        },
        avatar: {
            [theme.breakpoints.down('sm')]: {
                width: '12px',
                height: '12px'
            },
            [theme.breakpoints.only('md')]: {
                width: '14px',
                height: '14px'
            },
            [theme.breakpoints.only('lg')]: {
                width: '16px',
                height: '16px'
            },
            [theme.breakpoints.only('xl')]: {
                width: '18px',
                height: '18px'
            }
        },
        repeatIcon: {
            [theme.breakpoints.down('sm')]: {
                fontSize: '8px',
                marginRight: 3,
                paddingTop: 1
            },
            [theme.breakpoints.only('md')]: {
                fontSize: '10px',
                marginRight: 4,
                paddingTop: 2
            },
            [theme.breakpoints.only('lg')]: {
                fontSize: '12px',
                marginRight: 4,
                paddingTop: 2
            },
            [theme.breakpoints.only('xl')]: {
                fontSize: '14px',
                marginRight: 4,
                paddingTop: 3
            }
        },
        duration: {
            fontFamily: 'Poppins',
            whiteSpace: 'nowrap',
            overflow: 'hidden !important',
            wordWrap: 'break-word',
            [theme.breakpoints.down('sm')]: {
                fontSize: '5px',
                paddingTop: '2px'
            },
            [theme.breakpoints.only('md')]: {
                fontSize: '7px',
                paddingTop: '2px'
            },
            [theme.breakpoints.only('lg')]: {
                fontSize: '10px',
                paddingTop: '1px'
            },
            [theme.breakpoints.only('xl')]: {
                fontSize: '12px',
                paddingTop: '1px'
            }
        },
        durationLabel: {
            fontWeight: 500
        },
        staffContainer: {
            width: 'auto',
            display: 'flex'
        },
        rightIndicators: {
            display: 'flex',
            alignItems: 'center',
            marginLeft: 'auto',
            flexShrink: 0,
            [theme.breakpoints.down('sm')]: {
                gap: '4px'
            },
            [theme.breakpoints.only('md')]: {
                gap: '6px'
            },
            [theme.breakpoints.only('lg')]: {
                gap: '8px'
            },
            [theme.breakpoints.only('xl')]: {
                gap: '10px'
            }
        },
        inHomeIndicator: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#E4DDD5',
            borderRadius: '50%',
            flexShrink: 0,
            width: 14,
            height: 14,

            [theme.breakpoints.up('lg')]: {
                width: 24,
                height: 24
            }
        },
        inHomeIcon: {
            fontSize: 7,
            color: '#111111',

            [theme.breakpoints.up('lg')]: {
                fontSize: 13
            }
        },
        requestChip: {
            'height': 'auto',
            'color': '#ffffff',
            'padding': '2px 7px',
            'backgroundColor': '#EAB464',
            'marginBottom': 4,

            '& .MuiChip-label': {
                fontSize: 8,
                lineHeight: 1,
                fontWeight: 600
            },

            '@container eventCard (min-height: 15px)': {
                'padding': '4px 7px',

                '& .MuiChip-label': {
                    fontSize: 10
                }
            },
            '@container eventCard (min-height: 30px)': {
                'padding': '5px 11px',

                '& .MuiChip-label': {
                    fontSize: 13
                }
            }
        },
        color: {
            borderLeft: '6px solid transparent'
        }
    })
);

export const EventFullCalendar: FunctionComponent<EventFullCalendarProps> = props => {
    const classes = useStyles();
    const renderedEventFrom = props.event.start ? moment(props.event.start) : props.event.extendedProps.from.clone();
    const renderedEventTo = props.event.end ? moment(props.event.end) : props.event.extendedProps.to.clone();
    const serviceDurationInMinutes = renderedEventTo.diff(renderedEventFrom, 'minutes');
    const isExtendedVersion = serviceDurationInMinutes >= 30;
    const showBreed = serviceDurationInMinutes > 15 && props.event.extendedProps.petBreed !== 'Not Defined';
    const isSessionEvent = props.event.extendedProps.isSession === true;
    const sessionDurationInMinutes = Math.max(0, renderedEventTo.diff(props.event.extendedProps.to, 'minutes'));
    const showInHomeIndicator = props.event.extendedProps.isInHomeSession === true;
    const showWeeklyStaff = props.event.extendedProps.format === 'weekly' && isExtendedVersion;
    const showTopRight = !isExtendedVersion || showWeeklyStaff || showInHomeIndicator;
    const services = useSelector<RootState, Array<Service>>(state => state.services.services);
    const serviceColor = services.find(service => service.id === props.event.extendedProps.serviceId)?.color;

    const formatSessionDuration = (minutes: number) => {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;

        if (hours > 0 && remainingMinutes > 0) {
            return `${hours}h ${remainingMinutes}m`;
        }

        if (hours > 0) {
            return `${hours}h`;
        }

        return `${remainingMinutes}m`;
    };

    const duration = isSessionEvent ? (
        <Box>
            <Typography className={classes.duration}>
                {`Start Window: ${props.event.extendedProps.from.format(
                    'h:mm'
                )} - ${props.event.extendedProps.to.format('h:mm a')}`}
            </Typography>
            <Typography className={classes.duration}>
                <span className={classes.durationLabel}>Duration:</span>{' '}
                {formatSessionDuration(sessionDurationInMinutes)}
            </Typography>
        </Box>
    ) : (
        <Typography className={classes.duration}>
            {`${props.event.extendedProps.from.format('h:mm a')} - ${props.event.extendedProps.to.format('h:mm a')}`}
        </Typography>
    );

    return (
        <Box
            id={`calendar_div_event_${props.event.extendedProps.appointmentId}`}
            className={clsx(
                classes.container,
                {
                    [classes.booked]: props.event.extendedProps.status.id === status.BOOKED
                },
                {
                    [classes.confirmed]: props.event.extendedProps.status.id === status.CONFIRMED
                },
                {
                    [classes.checkedIn]: props.event.extendedProps.status.id === status.CHECKED_IN
                },
                {
                    [classes.inProgress]: props.event.extendedProps.status.id === status.IN_PROGRESS
                },
                {
                    [classes.readyForCheckout]: props.event.extendedProps.status.id === status.READY_FOR_CHECK_OUT
                },
                {
                    [classes.payment]: props.event.extendedProps.status.id === status.PAYMENT
                },
                {
                    [classes.completed]: props.event.extendedProps.status.id === status.COMPLETED
                },
                {
                    [classes.requested]: props.event.extendedProps.status.id === status.REQUESTED
                },
                {
                    [classes.loading]: props.loading
                },
                {
                    [classes.color]: serviceColor
                }
            )}
            style={{ borderLeftColor: serviceColor ?? undefined }}
        >
            {props.loading ? (
                <Box className={classes.spinnerContainer}>
                    <Spinner classNameCircularProgress={classes.spinner} />
                </Box>
            ) : (
                <Box className={classes.cardContainer}>
                    <Box>
                        {props.event.extendedProps.status.id === status.REQUESTED && (
                            <Chip
                                label="Request"
                                className={classes.requestChip}
                            />
                        )}

                        <Box className={classes.spacedContainer}>
                            <Box className={clsx(classes.row, classes.petNameContainer)}>
                                <Typography className={classes.petName}>{props.event.extendedProps.petName}</Typography>
                                {props.event.extendedProps.petHasMedicalConditions && (
                                    <FontAwesomeIcon
                                        icon={faHeartbeat}
                                        className={classes.heartIcon}
                                    />
                                )}
                                {props.event.extendedProps.petHasNegativePetTags && (
                                    <FontAwesomeIcon
                                        icon={faTriangleExclamation}
                                        className={clsx(classes.heartIcon, classes.specialCareIcon)}
                                    />
                                )}
                            </Box>
                            {showTopRight && (
                                <Box className={classes.rightIndicators}>
                                    {!isExtendedVersion && duration}
                                    {showWeeklyStaff && (
                                        <Box className={classes.staffContainer}>
                                            {props.event.extendedProps.recurringUuid && isExtendedVersion && (
                                                <FontAwesomeIcon
                                                    icon={faArrowsRepeat}
                                                    className={classes.repeatIcon}
                                                />
                                            )}
                                            <Avatar
                                                src={props.event.extendedProps.staffAvatar}
                                                className={classes.avatar}
                                            />
                                        </Box>
                                    )}
                                    {showInHomeIndicator && (
                                        <Box className={classes.inHomeIndicator}>
                                            <FontAwesomeIcon
                                                icon={faHouse}
                                                className={classes.inHomeIcon}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                        {showBreed && (
                            <Box className={classes.row}>
                                <Typography className={classes.text}>{props.event.extendedProps.petBreed}</Typography>
                            </Box>
                        )}
                        {isExtendedVersion && (
                            <>
                                <Box className={classes.row}>
                                    <FontAwesomeIcon
                                        icon={faCircleUser}
                                        className={classes.userIcon}
                                    />
                                    <Typography className={classes.text}>
                                        {`${props.event.extendedProps.client?.firstName} ${props.event.extendedProps.client?.lastName}`}
                                    </Typography>
                                </Box>
                                <Box className={classes.spacedContainer}>
                                    <Typography className={classes.duration}>
                                        {props.event.extendedProps.serviceName}
                                    </Typography>
                                </Box>
                                {duration}
                            </>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default EventFullCalendar;
